import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const isOpaqueId = (value) => /^[a-f0-9]{32,}$/i.test((value || '').trim());

const obfuscateEmail = (email) => {
  const raw = (email || '').trim();
  if (!raw.includes('@')) return '';
  const [local, domain] = raw.split('@');
  if (!local || !domain) return '';
  const visible = local.length <= 2 ? local[0] : local.slice(0, 2);
  return `${visible}***@${domain}`;
};

const HomePage = () => {
  const { user } = useAuth();
  const rawName = user?.name || '';
  const rawEmail = user?.email || '';
  const safeName = !isOpaqueId(rawName) ? rawName : '';
  const obfuscatedEmail = obfuscateEmail(rawEmail);
  const userDisplayName = safeName || obfuscatedEmail || 'Authenticated User';

  return (
    <div className="fade-in">
      <section className="hero-panel" style={{ marginBottom: '2rem' }}>
        <div className="hero-panel__content">
          <p className="eyebrow">Welcome to DocMerge</p>
          <h1>Your guide to merging clinical policy documents.</h1>
          <p className="hero-copy">
            This tool helps you combine a template document with practice-specific documents into a single, unified policy.
          </p>
          {user ? (
            <div style={{ marginTop: '1rem' }}>
              <Link to="/dashboard" className="btn btn-primary">
                Go to Dashboard
              </Link>
            </div>
          ) : (
            <div style={{ marginTop: '1rem' }}>
              <Link to="/login" className="btn btn-primary">
                Sign In to Start
              </Link>
            </div>
          )}
        </div>

        <div className="status-card">
          {user ? (
            <>
              <h2>Hello, {userDisplayName}</h2>
              <p className="muted">You are ready to start merging.</p>
            </>
          ) : (
            <>
              <h2>Guest Access</h2>
              <p className="muted">Please sign in to access your projects.</p>
            </>
          )}
        </div>
      </section>

      <section>
        <h2 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>How it works</h2>
        <div className="feature-grid">
          <article className="feature-card">
            <h3 className="eyebrow" style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>Step 1</h3>
            <h3>Create a Project</h3>
            <p>
              Navigate to your <Link to="/dashboard" style={{ textDecoration: 'underline' }}>Dashboard</Link> and click the
              <strong> New Project</strong> button. Give your project a clear name (e.g., "Asthma Policy 2024").
            </p>
          </article>

          <article className="feature-card">
            <h3 className="eyebrow" style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>Step 2</h3>
            <h3>Upload Documents</h3>
            <p>
              Upload your <strong>Template</strong> (the standard policy) and one or more <strong>Practice Documents</strong>.
              The system will acknowledge receipt of files.
            </p>
          </article>

          <article className="feature-card">
            <h3 className="eyebrow" style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>Step 3</h3>
            <h3>Generate & Review</h3>
            <p>
              Click <strong>Merge & Analyze</strong>. The AI will combine the documents and highlight any ambiguities or conflicts for you to review in the interactive editor.
            </p>
          </article>

          <article className="feature-card">
            <h3 className="eyebrow" style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>Step 4</h3>
            <h3>Finalize & Export</h3>
            <p>
              Once you have resolved all flags, you can export your finalized policy as a <strong>.docx</strong>, <strong>.pdf</strong>, or <strong>.txt</strong> file.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
